import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mic, Square, Send, Phone, Sparkles, Save, PhoneCall, HelpCircle, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import SashaAvatar from "../components/SashaAvatar";
import BudgetTable from "../components/BudgetTable";
import ItineraryOfferCards from "../components/ItineraryOfferCards";
import SaveTripModal from "../components/SaveTripModal";
import RingMeNowButton from "../components/RingMeNowButton";
import useAudioUnlock from "../hooks/useAudioUnlock";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Curated prompt suggestions that pre-populate the Ask-if input. These are
 * intentionally short, brand-relevant, and split between trip-quality
 * questions (left) and logistics questions (right) so the row stays
 * scannable. Lower-cased so they read like the user's voice when sent.
 */
const ASK_IF_SUGGESTIONS = [
  "non-stop flights available",
  "business or first class",
  "private balcony or terrace",
  "pet-friendly",
  "spa or pool on-site",
  "include airport transfer",
  "any kid-friendly options",
  "vegetarian or gluten-free dining",
];

/**
 * Quick-reply companion chip. Lets the guest ask Sasha a question about the
 * options displayed (rather than picking one). Collapsed by default so it
 * doesn't dominate the row of brand-styled choices; expands into a small
 * inline input with brand-curated suggestion chips. Pressing Enter or the
 * send icon submits and resets.
 */
function AskIfChip({ disabled, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-dashed border-[var(--brand-red)]/60 text-[var(--brand-red)] hover:bg-[var(--brand-red)] hover:text-[var(--ivory)] hover:border-[var(--brand-red)] transition-all duration-200 inline-flex items-center gap-1.5 disabled:opacity-40"
        data-testid="quick-reply-ask-if"
      >
        <HelpCircle size={12} strokeWidth={1.75} /> Ask if…
      </button>
    );
  }

  const submit = (raw) => {
    const v = (raw ?? value).trim();
    if (!v) return;
    onSubmit(v);
    setValue("");
    setOpen(false);
  };

  return (
    <div className="w-full" data-testid="ask-if-input-wrap">
      <div className="flex items-center gap-1 border border-[var(--brand-red)] bg-[var(--ivory)] pl-3 pr-1 py-1 max-w-[520px]">
        <input
          ref={inputRef}
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") {
              setValue("");
              setOpen(false);
            }
          }}
          placeholder="Ask if these options have…"
          className="flex-1 bg-transparent text-sm text-[var(--ink)] placeholder:text-[var(--muted)] outline-none min-w-[180px]"
          data-testid="ask-if-input"
        />
        <button
          type="button"
          onClick={() => {
            setValue("");
            setOpen(false);
          }}
          className="text-[var(--muted)] hover:text-[var(--brand-red)] p-1"
          aria-label="Cancel ask-if"
          data-testid="ask-if-cancel"
        >
          <X size={14} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          onClick={() => submit()}
          disabled={disabled || !value.trim()}
          className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] bg-[var(--brand-red)] text-[var(--ivory)] hover:opacity-90 disabled:opacity-40"
          data-testid="ask-if-submit"
        >
          <Send size={11} strokeWidth={1.5} />
        </button>
      </div>
      {/* Suggestion chips — single-tap to send a common follow-up. */}
      <div className="flex flex-wrap gap-1.5 mt-2 max-w-[520px]" data-testid="ask-if-suggestions">
        {ASK_IF_SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            disabled={disabled}
            onClick={() => submit(s)}
            className="text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 border border-[var(--brand-red)]/30 text-[var(--brand-red)] hover:bg-[var(--brand-red)] hover:text-[var(--ivory)] hover:border-[var(--brand-red)] transition-colors duration-200 disabled:opacity-40"
            data-testid={`ask-if-suggestion-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

const newId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// Concierge phone used by the "Call a live agent" button. Kept as a constant
// here because the chat page is rendered before the backend config call may
// resolve; the backend exposes the same number at /api/guest-contact/config
// which we fetch on mount to stay in sync if it ever changes.
const DEFAULT_CONCIERGE_PHONE = "+1 445 45Jante (52683)";

export default function ChatPage({ persona = "sasha" } = {}) {
  // Mobile-browser audio autoplay unlock — must run on the page that plays TTS.
  useAudioUnlock();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  // ── Topic detection ────────────────────────────────────────────────────
  // When the chat is opened from the Hosts page (Chat with Sasha CTA), the
  // URL carries `?topic=hosting`. Sasha then greets in hosting mode instead
  // of travel mode. Same chat process — different welcome bubble + quick
  // replies. Defaults to travel for any other arrival path.
  const [searchParams] = useSearchParams();
  const initialTopic = searchParams.get("topic") === "hosting" ? "hosting" : "travel";
  // Persona-specific copy. Sasha covers BOTH travel and hosting; the topic
  // flag flips which welcome + quick replies she leads with.
  const PERSONA_COPY = {
    sasha: initialTopic === "hosting" ? {
      name: "Sasha",
      welcomeBody:
        (user?.name ? `Welcome, ${user.name.split(' ')[0]}! ` : "Welcome! ") +
        "Glad you're thinking about hosting with Janté. Tell me about your property and I'll walk you through Bronze, Silver, and Gold — including the early-adopter window (6 months free if you sign by July 1, 2026). What would you like to explore first?",
      quickReplies: [
        {
          label: "Compare host tiers",
          value: "Walk me through Bronze, Silver, and Gold side by side — what do I get at each level and why would I upgrade?",
          mode: "explore",
        },
        {
          label: "What does the 20% commission cover?",
          value: "Explain the 20% commission — what services does it include and how does it compare to Airbnb hosting?",
          mode: "explore",
        },
        {
          label: "Tenant Thrive vs Hosting",
          value: "What's the difference between Tenant Thrive (long-term tenant placement) and the Host tiers (short-term)?",
          mode: "explore",
        },
        {
          label: "Plan a trip instead",
          value: "Actually, I'd like to plan a trip — help me think through destinations, budgets, and timing.",
          mode: "explore",
        },
      ],
      tripRefLine: (ref) =>
        `Welcome to Janté Hosts. Your host reference for today is ${ref} — quote it any time you speak with our hosting team.\n\n`,
      busyLabel: "Sasha is responding",
      thinkingLabel: "Sasha is answering — one moment…",
      ttsVoice: "nova",
    } : {
      name: "Sasha",
      welcomeBody:
        (user?.name ? `Welcome back, ${user.name.split(' ')[0]}! ` : "Hi! ") +
        "I'm so happy you're a part of the Janté family. Would you like to explore your options, book a trip with a concierge, or become a member?",
      quickReplies: [
        {
          label: "Explore travel options",
          value: "I'd like to explore travel options — help me think through destinations, budgets, and timing.",
          mode: "explore",
        },
        { label: "Book a trip", value: "__book_mode__", mode: "book" },
        { label: "Join Club Janté", value: "__nav__/membership" },
        {
          label: "Become a host",
          value: "I'd like to list a property with Janté. Walk me through Bronze, Silver, and Gold — including the early-adopter offer (6 months free if signed by July 1, 2026).",
          mode: "explore",
        },
      ],
      tripRefLine: (ref) =>
        `Welcome to Janté Travel Concierges. Your trip reference for today is ${ref} — quote it any time you speak with our concierge team.\n\n`,
      busyLabel: "Sasha is responding",
      thinkingLabel: "Sasha is answering — one moment…",
      ttsVoice: "nova",
    },
    corina: {
      name: "Corina",
      welcomeBody:
        (user?.name ? `Lovely to meet you, ${user.name.split(' ')[0]}! ` : "Lovely to meet you! ") +
        "I'm Corina, your hosts concierge at Janté. Tell me about your property and I'll walk you through Bronze, Silver, and Gold — including the 6 months free if you sign by July 1st. What would you like to explore first?",
      quickReplies: [
        {
          label: "Compare host tiers",
          value: "Walk me through Bronze, Silver and Gold side by side — what do I get at each level and why would I upgrade?",
        },
        {
          label: "What's the 20% commission cover?",
          value: "Explain the 20% commission — what services does it include and how does it compare to Airbnb hosting?",
        },
        {
          label: "Tenant Thrive vs Hosting",
          value: "What's the difference between Tenant Thrive (long-term tenant placement) and the Host tiers (short-term)?",
        },
        { label: "Talk to Sasha (travel)", value: "__nav__/talk-to-sasha" },
      ],
      tripRefLine: (ref) =>
        `Welcome to Janté Hosts. Your host reference for today is ${ref} — quote it any time you speak with our hosting team.\n\n`,
      busyLabel: "Corina is responding",
      thinkingLabel: "Corina is thinking — one moment…",
      ttsVoice: "shimmer", // a slightly warmer / softer voice than Sasha's "nova"
    },
  };
  const COPY = PERSONA_COPY[persona] || PERSONA_COPY.sasha;
  const navigate = useNavigate();
  const intakeMode = searchParams.get("intake") === "concierge";
  // Persist session id + chat transcript across page navigations so users can
  // wander to /membership, /jt-essentials, /portal, etc. and come back to find
  // Sasha exactly where they left her. sessionStorage auto-clears the moment
  // the tab closes — i.e., "exit site is the trigger" for a fresh session.
  //
  // Keys are namespaced by persona AND topic so the Sasha (travel) and
  // Sasha (hosts) chats never see each other's transcripts. Before this
  // namespacing, opening /talk-to-sasha?topic=hosting could restore the
  // prior travel welcome and quick replies (or vice-versa).
  const SK = {
    session: `jante_session_id__${persona}__${initialTopic}`,
    trip: `jante_trip_ref__${persona}__${initialTopic}`,
    messages: `jante_chat_messages__${persona}__${initialTopic}`,
    welcome: `jante_welcome_spoken__${persona}__${initialTopic}`,
  };
  const [sessionId] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem(SK.session);
      if (stored) return stored;
    } catch {
      /* storage unavailable */
    }
    const fresh = newId();
    try {
      window.sessionStorage.setItem(SK.session, fresh);
    } catch {
      /* storage unavailable */
    }
    return fresh;
  });
  const [tripRef, setTripRef] = useState(() => {
    try {
      return window.sessionStorage.getItem(SK.trip) || null;
    } catch {
      return null;
    }
  });
  // Which quick-reply the guest chose from the welcome. Null until the user
  // picks. "explore" = strategy + self-booking links · "book" = concierge-led.
  const [tripMode, setTripMode] = useState(intakeMode ? "book" : null);
  const [conciergePhone, setConciergePhone] = useState(DEFAULT_CONCIERGE_PHONE);
  // For paid users coming from checkout: a small inline intake form that
  // collects the rest of their concierge profile and emails it to the team.
  const [intakeOpen, setIntakeOpen] = useState(intakeMode);
  const [intakeForm, setIntakeForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    home_city: "",
    typical_travelers: "",
    travel_style: "",
    next_trip: "",
    constraints: "",
  });
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [intakeDone, setIntakeDone] = useState(false);
  const [messages, setMessages] = useState(() => {
    // Hydrate prior chat transcript from sessionStorage if the user already
    // started a conversation in this tab and just navigated back here.
    try {
      const stored = window.sessionStorage.getItem(SK.messages);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((m) => ({ ...m, streamed: true }));
        }
      }
    } catch {
      /* invalid JSON or storage unavailable */
    }
    return intakeMode ? [
    {
      cid: "welcome-intake",
      role: "ai",
      text:
        "Wonderful — welcome to the Janté family. Before we plan your first trip, our concierge will review a short intake. It captures how you travel, who travels with you, and anything we should know in advance. Once you submit it, your dedicated concierge will reach out within 24 hours to begin.",
      streamed: true,
    },
  ] : [
    {
      cid: "welcome",
      role: "ai",
      text: COPY.welcomeBody,
      streamed: true,
      quickReplies: COPY.quickReplies,
    },
  ];
  });
  // Fetch a fresh trip reference on mount and weave it into the welcome message.
  // Skip the fetch if we already restored one from sessionStorage — keeps the
  // chat continuous across page navigations within the same browser tab.
  useEffect(() => {
    if (tripRef) return; // already have one for this tab
    let mounted = true;
    api
      .get("/trip-ref")
      .then((r) => {
        if (!mounted) return;
        const ref = r.data?.trip_ref;
        if (!ref) return;
        setTripRef(ref);
        try {
          window.sessionStorage.setItem(SK.trip, ref);
        } catch {
          /* storage unavailable */
        }
        // Insert the trip reference line just above the welcome's quick replies
        setMessages((m) => {
          if (!m.length) return m;
          const head = { ...m[0] };
          if (head.role === "ai" && !head._refInjected) {
            head.text = COPY.tripRefLine(ref) + head.text;
            head._refInjected = true;
          }
          return [head, ...m.slice(1)];
        });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Pull the concierge phone from backend config on mount (keeps in sync with env).
  useEffect(() => {
    let mounted = true;
    api
      .get("/guest-contact/config")
      .then((r) => {
        if (!mounted) return;
        if (r.data?.emergency_phone) {
          const raw = r.data.emergency_phone;
          const m = raw.replace(/[^\d]/g, "").match(/^1?(\d{3})(\d{3})(\d{4})$/);
          setConciergePhone(m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : raw);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // If the guest is returning from a HubSpot payment, greet them contextually.
  // The offer-cards component persists `jante_offer_return` to localStorage just
  // before redirecting; we consume it once on mount.
  useEffect(() => {
    let stored = null;
    try {
      stored = window.localStorage.getItem("jante_offer_return");
    } catch {
      /* storage unavailable */
    }
    if (!stored) return;
    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      window.localStorage.removeItem("jante_offer_return");
      return;
    }
    // Only consume returns younger than 60 minutes, otherwise stale.
    const ageMs = Date.now() - new Date(parsed.at).getTime();
    if (!parsed?.option || ageMs > 60 * 60 * 1000) {
      window.localStorage.removeItem("jante_offer_return");
      return;
    }
    window.localStorage.removeItem("jante_offer_return");
    // Compose a friendly welcome-back message that mentions the next-step nudge.
    let welcomeBack;
    if (parsed.option === "email") {
      welcomeBack =
        "Welcome back! Your itinerary is on its way to your inbox — beautifully formatted, ready to print. " +
        "Now, may I share something quickly? If you find yourself dreaming up trips like this one regularly, " +
        "a Janté membership pays for itself the second time you travel — unlimited changes, member-only sales, " +
        "and a dedicated concierge so you never coordinate alone again. Would you like to see your tier options?";
    } else if (parsed.option === "membership") {
      welcomeBack =
        "Welcome to Janté! Now that you're a member, let's get you booked. " +
        "I'll start with flights, then ground transport and your stay — and I'll loop in your dedicated concierge " +
        "to handle anything bespoke. Ready to start with the flights?";
    } else if (parsed.option === "concierge") {
      welcomeBack =
        "Wonderful — your concierge appointment is confirmed. While you wait for the call, " +
        "is there anything else I can refine on this itinerary so they have the latest version in front of them?";
    }
    if (!welcomeBack) return;
    // Inject as a Sasha message so it streams + speaks.
    setTimeout(() => {
      // Force Sasha to speak this directly — don't round-trip the LLM.
      // We push a streaming AI bubble identical to what the chat handler does.
      (async () => {
        setStreaming(true);
        setSpeaking(true);
        let durationMs = Math.min(12000, Math.max(2200, welcomeBack.length * 55));
        try {
          const r = await api.post("/voice/tts", { text: welcomeBack, voice: COPY.ttsVoice });
          const audio = new Audio(`data:${r.data.mime};base64,${r.data.audio_base64}`);
          audioRef.current = audio;
          await new Promise((resolve) => {
            audio.addEventListener("loadedmetadata", resolve, { once: true });
            audio.addEventListener("error", resolve, { once: true });
            setTimeout(resolve, 1200);
          });
          if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
            durationMs = Math.floor(audio.duration * 1000);
          }
          audio.play().catch(() => {});
          audio.onended = () => setSpeaking(false);
        } catch {
          /* swallow */
        }
        await streamIntoLastMessage(welcomeBack, durationMs, null, false);
        setStreaming(false);
      })();
    }, 1800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [text, setText] = useState("");
  const [thinking, setThinking] = useState(false); // waiting for API response
  const [streaming, setStreaming] = useState(false); // actively streaming reply text + audio
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transferShown, setTransferShown] = useState(false);
  const [suggestedDest, setSuggestedDest] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  // The whole pipeline is "busy" while Sasha is thinking, streaming, or speaking — block any new input.
  const busy = thinking || streaming || speaking || recording;
  // Index of the last AI message — only that bubble's avatar should animate.
  const lastAiIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "ai") return i;
    }
    return -1;
  })();

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const streamTimerRef = useRef(null);
  const thinkingInterjectionRef = useRef(null);
  const welcomeSpokenRef = useRef(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Persist chat transcript to sessionStorage on every change so it survives
  // page navigations within this tab. Trim streaming-in-progress messages so
  // we don't restore a half-typed bubble. sessionStorage clears on tab close.
  useEffect(() => {
    try {
      const persistable = messages
        .filter((m) => !m.streaming)
        .map(({ streaming: _streaming, ...rest }) => rest);
      window.sessionStorage.setItem("jante_chat_messages", JSON.stringify(persistable));
    } catch {
      /* storage unavailable or quota exceeded */
    }
  }, [messages]);

  // Cleanup any pending stream timer on unmount
  useEffect(() => {
    return () => {
      if (streamTimerRef.current) clearInterval(streamTimerRef.current);
      if (thinkingInterjectionRef.current) clearTimeout(thinkingInterjectionRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const streamIntoLastMessage = (fullText, durationMs, budget, offerItinerary) =>
    new Promise((resolve) => {
      if (streamTimerRef.current) clearInterval(streamTimerRef.current);
      setMessages((m) => [...m, { cid: newId(), role: "ai", text: "", streaming: true, budget, offerItinerary }]);
      const chars = [...fullText];
      const stepMs = Math.max(14, Math.floor(durationMs / Math.max(chars.length, 1)));
      let i = 0;
      streamTimerRef.current = setInterval(() => {
        i += 1;
        const slice = chars.slice(0, i).join("");
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "ai" && last.streaming) {
            next[next.length - 1] = { ...last, text: slice };
          }
          return next;
        });
        if (i >= chars.length) {
          clearInterval(streamTimerRef.current);
          streamTimerRef.current = null;
          setMessages((m) => {
            const next = [...m];
            const last = next[next.length - 1];
            if (last && last.role === "ai" && last.streaming) {
              next[next.length - 1] = { cid: last.cid, role: "ai", text: fullText, streamed: true, budget, offerItinerary };
            }
            return next;
          });
          resolve();
        }
      }, stepMs);
    });

  const speakAndStream = async (replyText, budget, offerItinerary) => {
    setStreaming(true);
    // Engage speaking animation immediately so the avatar is visibly alive
    // even while we wait for TTS metadata. We'll keep it on through audio playback.
    setSpeaking(true);

    let durationMs = Math.min(12000, Math.max(2200, replyText.length * 55));
    let audioReady = false;
    let audioDone = new Promise((r) => r()); // resolved by default

    try {
      const r = await api.post("/voice/tts", { text: replyText, voice: COPY.ttsVoice });
      const audio = new Audio(`data:${r.data.mime};base64,${r.data.audio_base64}`);
      audioRef.current = audio;
      // Wait briefly for metadata so we know the audio's duration
      await new Promise((resolve) => {
        const done = () => resolve();
        audio.addEventListener("loadedmetadata", done, { once: true });
        audio.addEventListener("error", done, { once: true });
        setTimeout(done, 1200);
      });
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        durationMs = Math.floor(audio.duration * 1000);
      }
      audioReady = true;
      audioDone = new Promise((resolve) => {
        audio.onended = () => resolve();
        audio.onerror = () => resolve();
      });
      audio.play().catch(() => {});
    } catch (e) {
      console.warn("TTS failed", e);
    }

    // Stream text in sync with audio
    await streamIntoLastMessage(replyText, durationMs, budget, offerItinerary);
    setStreaming(false);

    // Wait for audio to finish too (if it actually played)
    if (audioReady) {
      await audioDone;
    }
    setSpeaking(false);
  };

  // ── Welcome recitation ─────────────────────────────────────────────────
  // On first user gesture (required by mobile autoplay rules), Sasha recites
  // her welcome paragraph aloud — TTS only, the text is already on screen
  // so we don't re-stream it. One-shot per tab.
  //
  // NOTE: `messages.length` is in the dep array so the listener is attached
  // the moment the welcome bubble lands in state (sometimes a hair after
  // first mount, e.g. when AuthContext is hydrating). Without this, the
  // effect ran with an empty `messages` array, returned early, and Sasha
  // never spoke aloud.
  useEffect(() => {
    if (welcomeSpokenRef.current) return;
    if (intakeMode) return; // intake mode has its own form, skip recitation
    // Pull the first AI message's text — the welcome bubble.
    const first = messages[0];
    if (!first || first.role !== "ai" || !first.text) return;
    // Skip if the transcript was restored from sessionStorage (already past the welcome).
    try {
      if (window.sessionStorage.getItem(SK.welcome)) {
        welcomeSpokenRef.current = true;
        return;
      }
    } catch { /* ignore */ }

    const speakWelcome = async () => {
      if (welcomeSpokenRef.current) return;
      welcomeSpokenRef.current = true;
      try {
        window.sessionStorage.setItem("jante_welcome_spoken", "1");
      } catch { /* ignore */ }
      try {
        setSpeaking(true);
        const r = await api.post("/voice/tts", {
          text: first.text,
          voice: COPY.ttsVoice,
        });
        const audio = new Audio(`data:${r.data.mime};base64,${r.data.audio_base64}`);
        audioRef.current = audio;
        audio.onended = () => setSpeaking(false);
        audio.onerror = () => setSpeaking(false);
        await audio.play().catch(() => setSpeaking(false));
      } catch {
        setSpeaking(false);
      }
    };

    const handler = () => {
      speakWelcome();
      window.removeEventListener("click", handler, true);
      window.removeEventListener("touchstart", handler, true);
      window.removeEventListener("keydown", handler, true);
    };
    window.addEventListener("click", handler, true);
    window.addEventListener("touchstart", handler, true);
    window.addEventListener("keydown", handler, true);
    return () => {
      window.removeEventListener("click", handler, true);
      window.removeEventListener("touchstart", handler, true);
      window.removeEventListener("keydown", handler, true);
    };
  }, [messages.length, intakeMode]);

  // ── Thinking interjection ──────────────────────────────────────────────
  // If Sasha takes more than ~1.2s to respond, she politely excuses herself
  // aloud ("One moment, please…") so the silence isn't awkward. Triggered on
  // every send; cleared the moment the reply comes back.
  const THINKING_INTERJECTIONS = [
    "One moment, please…",
    "Let me think on that…",
    "Pardon me a moment…",
    "Bear with me one second…",
  ];
  const armThinkingInterjection = () => {
    if (thinkingInterjectionRef.current) clearTimeout(thinkingInterjectionRef.current);
    thinkingInterjectionRef.current = setTimeout(async () => {
      // Only speak if still thinking and audio is unlocked (we know it is —
      // the user just tapped send/quick-reply).
      const line = THINKING_INTERJECTIONS[
        Math.floor(Math.random() * THINKING_INTERJECTIONS.length)
      ];
      try {
        const r = await api.post("/voice/tts", { text: line, voice: COPY.ttsVoice });
        const audio = new Audio(`data:${r.data.mime};base64,${r.data.audio_base64}`);
        await audio.play().catch(() => {});
      } catch {
        /* swallow */
      }
    }, 1300);
  };
  const disarmThinkingInterjection = () => {
    if (thinkingInterjectionRef.current) {
      clearTimeout(thinkingInterjectionRef.current);
      thinkingInterjectionRef.current = null;
    }
  };

  const sendText = async (userText) => {
    if (busy) return; // hard guard — don't accept new input while Sasha is mid-answer
    const trimmed = userText.trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { cid: newId(), role: "user", text: trimmed }]);
    setText("");
    setThinking(true);
    armThinkingInterjection();
    try {
      const r = await api.post("/chat/sasha", {
        session_id: sessionId,
        text: trimmed,
        trip_ref: tripRef,
        mode: tripMode || "explore",
        language: i18n.language || "en",
        persona: persona,
      });
      const reply = r.data.reply || "(thinking...)";
      if (r.data.transfer_to_concierge) setTransferShown(true);
      if (r.data.suggested_destination) setSuggestedDest(r.data.suggested_destination);
      disarmThinkingInterjection();
      setThinking(false);
      await speakAndStream(reply, r.data.budget, r.data.offer_itinerary);
    } catch (e) {
      disarmThinkingInterjection();
      setThinking(false);
      toast.error(e?.response?.data?.detail || "Sasha is briefly unavailable.");
    }
  };

  const startRecording = async () => {
    if (busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { noiseSuppression: true, echoCancellation: true },
      });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        await transcribeAndSend(blob);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied. Please allow microphone permission.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAndSend = async (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "voice.webm");
    setThinking(true);
    try {
      const r = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcript = (r.data.text || "").trim();
      if (!transcript) {
        toast.message("I didn't catch that — try again?");
        setThinking(false);
        return;
      }
      // Inline behavior of sendText (we're already past the busy guard)
      setMessages((m) => [...m, { cid: newId(), role: "user", text: transcript }]);
      armThinkingInterjection();
      const ar = await api.post("/chat/sasha", {
        session_id: sessionId,
        text: transcript,
        trip_ref: tripRef,
        mode: tripMode || "explore",
        language: i18n.language || "en",
        persona: persona,
      });
      const reply = ar.data.reply || "(thinking...)";
      if (ar.data.transfer_to_concierge) setTransferShown(true);
      if (ar.data.suggested_destination) setSuggestedDest(ar.data.suggested_destination);
      disarmThinkingInterjection();
      setThinking(false);
      await speakAndStream(reply, ar.data.budget, ar.data.offer_itinerary);
    } catch (e) {
      disarmThinkingInterjection();
      setThinking(false);
      toast.error("Couldn't transcribe — please try again.");
    }
  };

  const saveAsItinerary = async () => {
    if (!user) {
      // Open the in-place sign-up + save modal so anonymous guests can create an
      // account and save the trip atomically (no auth detour mid-conversation).
      setSaveModalOpen(true);
      return;
    }
    const dest = suggestedDest || "Custom Destination";
    const today = new Date();
    const start = today.toISOString().slice(0, 10);
    const end = new Date(today.getTime() + 5 * 86400000).toISOString().slice(0, 10);
    try {
      const r = await api.post("/itineraries", {
        title: `Trip to ${dest}`,
        destination: dest,
        start_date: start,
        end_date: end,
        travelers: 2,
        notes: messages.map((m) => `${m.role === "user" ? "Guest" : "Sasha"}: ${m.text}`).join("\n\n"),
        items: [
          { type: "flight", title: `Flights to ${dest}`, provider: "preferred_flights" },
          { type: "car", title: `Rental Car`, provider: "preferred_cars" },
          { type: "activity", title: `Top Experiences`, provider: "preferred_activities" },
        ],
        recall_code: tripRef || undefined,
        budget: lastBudget(),
      });
      toast.success("Saved to your client portal.");
      setTimeout(() => (window.location.href = `/itinerary/${r.data.id}`), 700);
    } catch (e) {
      toast.error("Could not save — please try again.");
    }
  };

  // Find the most recent budget Sasha produced (for save-as-itinerary)
  const lastBudget = () => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].budget) return messages[i].budget;
    }
    return null;
  };

  const handleQuickReply = (qr) => {
    if (busy) return;
    if (qr.value && qr.value.startsWith("__nav__")) {
      window.location.href = qr.value.replace("__nav__", "");
      return;
    }
    // Lock-in the trip mode for this session if the quick-reply declares one.
    if (qr.mode) setTripMode(qr.mode);

    // "Book a Trip" special case — inject the concierge preamble locally
    // (no API call). Keeps the intro reliable and fast.
    if (qr.value === "__book_mode__") {
      setMessages((m) => [
        ...m,
        { cid: newId(), role: "user", text: "I'm ready to book a trip." },
        {
          cid: newId(),
          role: "ai",
          streamed: true,
          _bookPreamble: true,
          text:
            "Great — let's book it. Where are you flying from and to, which dates, and how many travelers?\n\n" +
            (tripRef
              ? `Your trip reference is ${tripRef}. Tap "Call a live agent" below any time and our concierge will call you back.\n\n`
              : "Keep your trip reference (shown above). Tap \"Call a live agent\" below any time and our concierge will call you back.\n\n") +
            `Urgent travel? Call ${conciergePhone} for the best rates.`,
        },
      ]);
      return;
    }

    sendText(qr.value);
  };

  // Trip Reference Recall — input where a returning guest can paste a saved
  // code to pick up an earlier session/itinerary.
  const [recallInput, setRecallInput] = useState("");
  const [recallBusy, setRecallBusy] = useState(false);
  const submitRecall = async (e) => {
    e.preventDefault();
    const code = recallInput.trim().toUpperCase();
    if (!code) return;
    setRecallBusy(true);
    try {
      const r = await api.get(`/recall/${encodeURIComponent(code)}`);
      const d = r.data || {};
      setTripRef(code);
      try {
        window.sessionStorage.setItem(SK.trip, code);
      } catch { /* ignore */ }
      const dest = d.trip?.destination || d.trip?.title || "your trip";
      const note =
        d.kind === "itinerary"
          ? `Pulled up your saved itinerary for ${dest} (reference ${code}). Want me to review or refine it?`
          : d.kind === "trip_ref_only"
          ? `Got it — recalled session ${code}. Tell me where we left off or what you'd like to plan now.`
          : d.kind === "concierge_transfer"
          ? `Recalled concierge transfer ${code}. The concierge has your details — pick up where you left off?`
          : `Recalled ${code} — how can I help?`;
      setMessages((m) => [
        ...m,
        { cid: newId(), role: "user", text: `Recall trip reference ${code}` },
        { cid: newId(), role: "ai", text: note, streamed: true, _recall: d },
      ]);
      setRecallInput("");
      toast.success(`Recalled ${code}.`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) {
        toast.error("That trip reference has expired (free-tier 30-day recall).");
      } else if (status === 404) {
        toast.error("No trip found for that reference.");
      } else {
        toast.error("Could not recall that reference. Try again?");
      }
    } finally {
      setRecallBusy(false);
    }
  };

  // Start new trip — explicit gesture to clear the conversation. We intentionally
  // do NOT auto-clear on idle / page navigation; the previous trip stays present
  // for the entire session until the client says "new booking".
  const startNewTrip = () => {
    if (!window.confirm("Start a new trip? Your current chat will be cleared.")) return;
    try {
      window.sessionStorage.removeItem("jante_chat_messages");
      window.sessionStorage.removeItem("jante_trip_ref");
      window.sessionStorage.removeItem("jante_session_id");
    } catch { /* ignore */ }
    window.location.reload();
  };

  const statusLabel = thinking
    ? "thinking…"
    : speaking
    ? "speaking…"
    : streaming
    ? "speaking…"
    : recording
    ? "listening…"
    : "ready";

  return (
    <div className="relative flex flex-col min-h-[calc(100vh-80px)] bg-[var(--ivory)]" data-testid="chat-page">
      {/* Trip reference banner — Sasha's session quote (sticky so the guest
          always sees their recall code as they scroll the conversation). */}
      {tripRef && (
        <div
          className="sticky top-0 z-20 bg-[var(--ivory)]/95 backdrop-blur-sm border-b border-[var(--gold)]/30 px-6 py-3 flex items-center gap-3 flex-wrap"
          data-testid="trip-ref-banner"
        >
          <div className="max-w-4xl mx-auto w-full flex items-center gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-red)]">
              Trip reference
            </div>
            <div
              className="text-sm uppercase tracking-[0.22em] text-[var(--emerald)] font-medium tabular-nums"
              data-testid="trip-ref-code"
            >
              {tripRef}
            </div>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(tripRef);
                toast.success("Trip reference copied.");
              }}
              className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] hover:text-[var(--emerald)]"
              data-testid="trip-ref-copy"
            >
              Copy
            </button>

            {/* Trip Reference Recall — temporarily hidden per concierge ops
                decision. Backend recall endpoints stay live; flip the flag to
                bring this control back. */}
            {false && (
            <form
              onSubmit={submitRecall}
              className="flex items-center gap-1.5"
              data-testid="trip-ref-recall-form"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-red)]">
                Trip reference recall
              </div>
              <input
                type="text"
                value={recallInput}
                onChange={(e) => setRecallInput(e.target.value.toUpperCase())}
                placeholder="JT-XXXX-XX"
                disabled={recallBusy}
                className="text-[11px] uppercase tracking-[0.16em] tabular-nums px-2 py-1 border border-[var(--gold)]/50 bg-[var(--ivory)] text-[var(--emerald)] focus:outline-none focus:border-[var(--emerald)] disabled:opacity-50 w-32"
                data-testid="trip-ref-recall-input"
              />
              <button
                type="submit"
                disabled={recallBusy || !recallInput.trim()}
                className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 bg-[var(--emerald)] text-[var(--ivory)] hover:bg-[var(--brand-red)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="trip-ref-recall-submit"
              >
                {recallBusy ? "…" : "Recall"}
              </button>
            </form>
            )}

            <button
              type="button"
              onClick={startNewTrip}
              className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-2)] hover:text-[var(--brand-red)]"
              data-testid="trip-ref-new-booking"
              title="Clear this chat and start a fresh trip reference"
            >
              + New booking
            </button>

            <Link
              to={`/jt-essentials?ref=${encodeURIComponent(tripRef)}`}
              className="ml-auto text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--gold)] text-[var(--emerald)] hover:bg-[var(--gold)] hover:text-[var(--ivory)] transition-colors"
              data-testid="trip-ref-jtc-essentials"
            >
              JTC Essentials
            </Link>
            <RingMeNowButton tripRef={tripRef} compact />
          </div>
        </div>
      )}

      {/* Concierge intake — shown only when user landed at /chat?intake=concierge */}
      {intakeOpen && (
        <div className="max-w-4xl mx-auto px-6 mt-6 w-full" data-testid="concierge-intake">
          <div className="lux-card">
            <div className="overline text-[var(--brand-red)]">Concierge intake</div>
            <h2 className="font-display text-3xl text-[var(--emerald)] mt-2 leading-tight">
              Help your concierge get to know you.
            </h2>
            {intakeDone ? (
              <div className="mt-6 text-center" data-testid="intake-success">
                <div className="font-display text-2xl text-[var(--emerald)]">
                  Thank you — your intake is on its way.
                </div>
                <p
                  className="mt-3 text-[var(--ink-2)]"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.15rem" }}
                >
                  Your dedicated concierge will reach out within 24 hours.
                  In the meantime &mdash; tell Sasha where you&rsquo;re going.
                </p>
                <button
                  type="button"
                  onClick={() => setIntakeOpen(false)}
                  className="btn-primary mt-5"
                  data-testid="intake-continue"
                >
                  Continue with Sasha
                </button>
              </div>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (intakeBusy) return;
                  setIntakeBusy(true);
                  try {
                    await api.post("/concierge-intake", {
                      ...intakeForm,
                      trip_ref: tripRef,
                      session_id: sessionId,
                    });
                    setIntakeDone(true);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
                  } finally {
                    setIntakeBusy(false);
                  }
                }}
                className="mt-6 grid sm:grid-cols-2 gap-4"
                data-testid="intake-form"
              >
                <div>
                  <div className="overline mb-1">Name</div>
                  <input className="lux-input" required value={intakeForm.name}
                    onChange={(e) => setIntakeForm({ ...intakeForm, name: e.target.value })}
                    data-testid="intake-name" />
                </div>
                <div>
                  <div className="overline mb-1">Email</div>
                  <input className="lux-input" type="email" required value={intakeForm.email}
                    onChange={(e) => setIntakeForm({ ...intakeForm, email: e.target.value })}
                    data-testid="intake-email" />
                </div>
                <div>
                  <div className="overline mb-1">Phone</div>
                  <input className="lux-input" required value={intakeForm.phone}
                    onChange={(e) => setIntakeForm({ ...intakeForm, phone: e.target.value })}
                    data-testid="intake-phone" />
                </div>
                <div>
                  <div className="overline mb-1">Home city</div>
                  <input className="lux-input" value={intakeForm.home_city}
                    onChange={(e) => setIntakeForm({ ...intakeForm, home_city: e.target.value })}
                    data-testid="intake-home-city" />
                </div>
                <div className="sm:col-span-2">
                  <div className="overline mb-1">Who usually travels with you?</div>
                  <input className="lux-input" placeholder="Partner · family · friends · solo"
                    value={intakeForm.typical_travelers}
                    onChange={(e) => setIntakeForm({ ...intakeForm, typical_travelers: e.target.value })}
                    data-testid="intake-travelers" />
                </div>
                <div className="sm:col-span-2">
                  <div className="overline mb-1">Travel style</div>
                  <input className="lux-input" placeholder="Adventurous · culinary · romantic · cultural · spa…"
                    value={intakeForm.travel_style}
                    onChange={(e) => setIntakeForm({ ...intakeForm, travel_style: e.target.value })}
                    data-testid="intake-style" />
                </div>
                <div className="sm:col-span-2">
                  <div className="overline mb-1">Next trip you have in mind</div>
                  <input className="lux-input" placeholder="Destination, dates, occasion"
                    value={intakeForm.next_trip}
                    onChange={(e) => setIntakeForm({ ...intakeForm, next_trip: e.target.value })}
                    data-testid="intake-next-trip" />
                </div>
                <div className="sm:col-span-2">
                  <div className="overline mb-1">Anything we should know?</div>
                  <textarea className="lux-input min-h-[90px]"
                    placeholder="Allergies, accessibility, must-haves, must-avoids…"
                    value={intakeForm.constraints}
                    onChange={(e) => setIntakeForm({ ...intakeForm, constraints: e.target.value })}
                    data-testid="intake-constraints" />
                </div>
                <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 pt-1">
                  <div className="text-[11px] text-[var(--muted)]">
                    Routed privately to Concierges@JanteTravelConcierges.com
                    {tripRef ? ` with trip reference ${tripRef}.` : "."}
                  </div>
                  <button type="submit" disabled={intakeBusy} className="btn-primary" data-testid="intake-submit">
                    {intakeBusy ? "Sending…" : "Submit intake"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Concierge-led mode banner — appears after user picks "Book a trip". */}
      {tripMode === "book" && (
        <div
          className="max-w-4xl mx-auto px-6 mt-4 w-full"
          data-testid="book-mode-banner"
        >
          <div className="border border-[var(--brand-red)]/30 bg-[var(--brand-red)]/5 p-4 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="overline text-[var(--brand-red)]">Concierge-led trip</div>
              <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">
                Sasha is drafting the bones — a Janté concierge will finalize every detail by email within 24 hours.
                {tripRef ? ` Reference ${tripRef} when you call.` : ""}
              </p>
            </div>
            <a
              href={`tel:${conciergePhone.replace(/[^\d+]/g, "")}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--brand-red)] text-[var(--ivory)] text-[11px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity shrink-0"
              data-testid="book-mode-call-agent"
              onClick={() => {
                if (tripRef) {
                  try { navigator.clipboard?.writeText(tripRef); } catch { /* clipboard unavailable */ }
                }
              }}
            >
              <PhoneCall size={13} strokeWidth={1.75} /> Call a live agent
            </a>
          </div>
        </div>
      )}

      {/* Suggested destination floating action (when Sasha picks one up) */}
      {suggestedDest && (
        <div className="max-w-4xl w-full mx-auto px-6 mt-6" data-testid="suggested-destination">
          <div className="lux-card flex items-center gap-4">
            <div className="flex-1">
              <div className="overline">Sasha senses</div>
              <div className="font-display text-2xl text-[var(--emerald)] mt-1">{suggestedDest}</div>
            </div>
            <button onClick={saveAsItinerary} className="btn-primary" data-testid="save-itinerary-btn">
              <Save size={14} strokeWidth={1.5} /> Save itinerary
            </button>
          </div>
        </div>
      )}

      {/* Chat scroll */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 py-8 lg:py-10 max-w-4xl w-full mx-auto space-y-4"
        data-testid="chat-scroll"
        onCopy={(e) => {
          // Only block copy when the selection is inside Sasha's content (not user's own input/bubble).
          const sel = window.getSelection?.();
          const node = sel?.anchorNode;
          const el = node?.nodeType === 1 ? node : node?.parentElement;
          if (el && el.closest && el.closest("[data-protected='true']")) {
            e.preventDefault();
            toast.message("Copying is disabled while booking APIs are being finalised.");
          }
        }}
        onContextMenu={(e) => {
          if (e.target.closest && e.target.closest("[data-protected='true']")) {
            e.preventDefault();
          }
        }}
      >
        {messages.map((m, i) => (
          <div key={m.cid || `msg-${i}`} className="flex flex-col">
            {m.role === "ai" && (
              <div className="flex items-end gap-4 mb-3" data-testid={`sasha-speaker-${i}`}>
                <div className="w-28 sm:w-32 shrink-0">
                  <SashaAvatar
                    active={i === lastAiIdx}
                    speaking={i === lastAiIdx && (speaking || streaming)}
                    listening={i === lastAiIdx && recording}
                    size="sm"
                    persona={persona}
                    showName={false}
                  />
                </div>
                <div className="pb-1">
                  <div className="font-display text-xl text-[var(--emerald)] leading-none">Janté</div>
                  {i === lastAiIdx && (
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--gold)] mt-1 flex items-center gap-1.5">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          speaking || streaming
                            ? "bg-[var(--gold)] animate-pulse"
                            : recording
                            ? "bg-[var(--burgundy)] animate-pulse"
                            : thinking
                            ? "bg-[var(--gold)] animate-pulse"
                            : "bg-[var(--emerald)]/40"
                        }`}
                      />
                      {statusLabel}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex">
              <div
                className={m.role === "user" ? "bubble-user" : "bubble-ai"}
                data-testid={`bubble-${m.role}-${i}`}
                data-protected={m.role === "ai" ? "true" : undefined}
              >
                {m.text}
                {m.streaming && (
                  <span className="stream-caret" aria-hidden="true">▍</span>
                )}
              </div>
            </div>
            {/* Itemized budget table — appears below Sasha's narrative */}
            {m.role === "ai" && m.budget && !m.streaming && (
              <BudgetTable
                budget={m.budget}
                sashaReply={m.text}
                onBook={() => sendText("Yes — please prepare booking links for this itinerary.")}
                onMembership={() => navigate("/membership")}
              />
            )}
            {/* Three-option "what next" chooser (membership · email · concierge) */}
            {m.role === "ai" && m.offerItinerary && !m.streaming && (
              <ItineraryOfferCards
                sessionId={sessionId}
                tripRef={tripRef}
                budget={m.budget || null}
                userInfo={user || null}
              />
            )}
            {m.quickReplies && !busy && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid={`quick-replies-${i}`}>
                {m.quickReplies.map((qr) => (
                  <button
                    key={qr.label}
                    onClick={() => handleQuickReply(qr)}
                    className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-[var(--gold)] text-[var(--emerald)] hover:bg-[var(--gold)] hover:text-[var(--ivory)] transition-all duration-200"
                    data-testid={`quick-reply-${qr.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {qr.label}
                  </button>
                ))}
                {/* "Ask if…" lets the guest pose a follow-up question about
                    the options shown instead of committing to one. Inline
                    expand → mini text input → send. Same handler as the
                    main send path so Sasha responds in the same thread. */}
                <AskIfChip
                  disabled={busy}
                  onSubmit={(question) => {
                    if (!question.trim()) return;
                    sendText(`Quick question about the options: ${question.trim()}`);
                  }}
                />
              </div>
            )}
          </div>
        ))}
        {thinking && (
          <div className="flex" data-testid="thinking-indicator">
            <div className="bubble-ai inline-flex items-center gap-1">
              <span className="dot-thinking" />
              <span className="dot-thinking" style={{ animationDelay: "150ms" }} />
              <span className="dot-thinking" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        {transferShown && (
          <div className="lux-card-dark mt-6" data-testid="transfer-prompt">
            <div className="overline text-[var(--gold)]">Sasha suggests</div>
            <div className="font-display text-2xl mt-2">A live concierge can help</div>
            <p className="mt-3 text-sm opacity-90">
              This trip deserves a personal touch. Connect with our concierge team to lock in the details.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={`tel:${conciergePhone.replace(/[^\d+]/g, "")}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--gold)] text-[var(--emerald)] text-[11px] uppercase tracking-[0.22em] hover:opacity-90 transition-opacity"
                data-testid="transfer-call-btn"
                onClick={() => {
                  if (tripRef) {
                    try { navigator.clipboard?.writeText(tripRef); } catch { /* clipboard unavailable */ }
                  }
                }}
              >
                <PhoneCall size={14} strokeWidth={1.5} /> Call a live agent
              </a>
              <a
                href="https://scheduler.zoom.us/jante-luxury-travel/membershipenrollmentreview"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost text-[var(--ivory)] border-[var(--ivory)]/40"
                data-testid="transfer-confirm-btn"
              >
                <Phone size={14} strokeWidth={1.25} /> Schedule callback
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Input bar (sticky bottom) */}
      <div className="sticky bottom-0 border-t border-[var(--emerald)]/10 bg-white">
        {busy && (
          <div className="max-w-4xl mx-auto px-6 pt-3" data-testid="busy-banner">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--gold)] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] animate-pulse" />
              Please wait — Sasha is responding.
            </div>
          </div>
        )}
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          {!recording ? (
            <button
              onClick={startRecording}
              disabled={busy}
              className="w-14 h-14 rounded-full border border-[var(--gold)] hover:bg-[var(--gold)] hover:text-white text-[var(--emerald)] flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={busy ? "Please wait — Sasha is responding" : "Hold to speak"}
              data-testid="mic-start-btn"
            >
              <Mic size={20} strokeWidth={1.5} />
            </button>
          ) : (
            <button
              onClick={stopRecording}
              className="w-14 h-14 rounded-full bg-[var(--burgundy)] text-white flex items-center justify-center animate-pulse"
              data-testid="mic-stop-btn"
            >
              <Square size={20} strokeWidth={1.5} />
            </button>
          )}
          <input
            className="lux-input flex-1"
            placeholder={busy ? "Sasha is answering — one moment…" : "Type — 'A quiet week in Kyoto for two in May...'"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !busy) sendText(text);
            }}
            disabled={busy}
            data-testid="chat-input"
          />
          <button
            onClick={() => sendText(text)}
            disabled={busy || !text.trim()}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="send-btn"
          >
            <Send size={14} strokeWidth={1.5} />
          </button>
        </div>
        <div className="text-center pb-3 text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] flex items-center justify-center gap-2">
          <Sparkles size={11} strokeWidth={1.25} /> Powered by Janté Travel Concierges intelligence
        </div>
      </div>

      <SaveTripModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSaved={(itineraryId) => {
          setSaveModalOpen(false);
          setTimeout(() => (window.location.href = `/itinerary/${itineraryId}`), 700);
        }}
        destination={suggestedDest || "Custom Destination"}
        tripRef={tripRef}
        budget={lastBudget()}
        messages={messages}
        travelers={2}
      />
    </div>
  );
}
